import { FC, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
	useGetTreatmentPlans,
	useDeleteTreatmentPlan,
} from "../../hooks/useTreatmentPlans";
import { useGetEvents } from "../../hooks/useEvents";
import { TreatmentPlan } from "../../domain/treatmentPlan.interface";
import TreatmentPlansList from "./TreatmentPlansList";
import TreatmentPlanForm from "./TreatmentPlanForm";
import AgentCompanion, {
	useAgentCompanion,
} from "../../components/AgentCompanion";

const TreatmentPlansComponent: FC = () => {
	const [showForm, setShowForm] = useState(false);
	const [editingPlan, setEditingPlan] = useState<TreatmentPlan | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [planToDelete, setPlanToDelete] = useState<string | null>(null);

	// The agent companion — the mascot for our agent-assisted features. This page is
	// internal-only, which makes it the right place to let the character be seen
	// while the team is still choosing between the three candidates.
	const { appearance, celebrate, dismiss } = useAgentCompanion();

	const {
		data: treatmentPlans,
		isLoading,
		error,
		refetch,
	} = useGetTreatmentPlans();
	const { data: events, isLoading: eventsLoading } = useGetEvents();
	const deleteMutation = useDeleteTreatmentPlan();

	const handleCreate = () => {
		setEditingPlan(null);
		setShowForm(true);
	};

	const handleEdit = (plan: TreatmentPlan) => {
		setEditingPlan(plan);
		setShowForm(true);
	};

	const handleClone = (plan: TreatmentPlan) => {
		// Create a clone without the _id, and append "Copy" to the name
		const clonedPlan: TreatmentPlan = {
			...plan,
			_id: "", // Will be generated by backend
			name: `${plan.name} (Copy)`,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};
		setEditingPlan(clonedPlan);
		setShowForm(true);
	};

	const handleCloseForm = () => {
		setShowForm(false);
		setEditingPlan(null);
		refetch();
	};

	const handleDeleteClick = (planId: string) => {
		setPlanToDelete(planId);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (planToDelete) {
			try {
				await deleteMutation.mutateAsync(planToDelete);
				setDeleteDialogOpen(false);
				setPlanToDelete(null);
				refetch();
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error deleting treatment plan:", err);
			}
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setPlanToDelete(null);
	};

	if (isLoading) return <Typography>Loading...</Typography>;
	if (error)
		return <Typography color="error">Error loading treatment plans</Typography>;

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}
			>
				<Typography variant="h4" component="h1">
					Treatment Plans
				</Typography>
				<Button
					variant="contained"
					color="primary"
					onClick={handleCreate}
					disabled={showForm}
				>
					Create Treatment Plan
				</Button>
			</Box>

			{/* Form Dialog */}
			{showForm && (
				<TreatmentPlanForm
					editingPlan={editingPlan}
					onClose={handleCloseForm}
					onSaved={celebrate}
					// A plan created without closing the form becomes the plan being
					// edited, so the assistant has an id to talk about.
					onPlanCreated={setEditingPlan}
					events={events}
					eventsLoading={eventsLoading}
				/>
			)}

			{/* Treatment Plans List */}
			{!showForm && (
				<>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Existing Treatment Plans
					</Typography>
					<TreatmentPlansList
						treatmentPlans={treatmentPlans}
						events={events}
						onEdit={handleEdit}
						onClone={handleClone}
						onDelete={handleDeleteClick}
					/>
				</>
			)}

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete this treatment plan? This action
						cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel}>Cancel</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<AgentCompanion appearance={appearance} onDismiss={dismiss} />
		</Box>
	);
};

export default TreatmentPlansComponent;
